import { buildUrl, usKidsGolfService } from '@/libs/api/instances/us-kids-golf-instance';
import type { RequestOptions } from '@/libs/api/types';

import { PlayerGuardianStatus } from './get-player-guardians';

/** Path (relative to `buildUrl`) for a single co-guardian's profile within a player's circle. */
export const playerCoGuardianEndpoint = (playerId: string, guardianUid: string) =>
  `/guardians/me/players/${playerId}/guardians/${guardianUid}`;

/** Mutual kid's mailing address (mirrors the endpoint's `address` object). Every field may be
 *  an empty string when unset. */
export type CoGuardianMutualKidAddress = {
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
};

/**
 * A kid both the primary guardian and this co-guardian can access (BR-02), as returned
 * inline on the co-guardian profile payload.
 *
 * NOTE: modelled by inference from the existing player/guardian DTOs — align the field
 * names with the backend once the endpoint contract is finalised.
 */
export type CoGuardianMutualKid = {
  id: string;
  name: string;
  /** Age in years; may be null when the birthday is unknown. */
  age?: number | null;
  /** Current pathway level. */
  level?: number | null;
  /** Avatar URL; may be empty for kids without a photo. */
  avatarUrl?: string;
  /** ISO date the co-guardian gained access to this kid (backend field). */
  createdOn?: string;
  since?: string;
  /** ISO date of birth, used for the "Birthdate" detail row. */
  dob?: string;
  /** e.g. "Male" | "Female". */
  gender?: string;
  /** Height in inches, used for the "Height" detail row (rendered as `48"`). */
  height?: number;
  /** e.g. "left" | "right". */
  dominantHand?: string;
  address?: CoGuardianMutualKidAddress;
};

/**
 * A co-guardian's profile for a specific player, from
 * GET `/api/v1/guardians/me/players/{playerId}/guardians/{guardianUid}`.
 *
 * Mirrors a single {@link PlayerGuardian} row (the circle list item) plus the inline
 * `mutualKids` list. Typed by inference from the existing DTOs.
 */
export type CoGuardianProfile = {
  guardianUid: string;
  name: string;
  /** Avatar URL; may be empty for members without a photo. */
  photoUrl?: string;
  /** Relationship to the player, e.g. "parent" | "grandparent" | "coach" | "other". */
  relationship?: string;
  /** Circle role, e.g. "owner" (primary guardian) | "coguardian". */
  role?: string;
  /** Membership status (not always present on the profile payload). */
  status?: PlayerGuardianStatus;
  /** ISO date the member joined the player's circle. */
  joinedSince?: string;
  /** Member's email. */
  email?: string;
  /** True for the signed-in guardian's own profile. */
  isSelf?: boolean;
  /** Kids shared between the primary guardian and this co-guardian (BR-02). */
  mutualKids: CoGuardianMutualKid[];
};

/**
 * Fetches a co-guardian's profile (details + mutual kids) for a player via
 * GET `/api/v1/guardians/me/players/{playerId}/guardians/{guardianUid}`. Authenticated
 * with the Firebase bearer token.
 */
export async function fetchCoGuardian(
  playerId: string,
  guardianUid: string,
  options?: RequestOptions,
): Promise<CoGuardianProfile> {
  return await usKidsGolfService.get<CoGuardianProfile>(
    buildUrl(playerCoGuardianEndpoint(playerId, guardianUid)),
    undefined,
    options,
  );
}
